import { CircleLoader, LoaderSizes, StandardPagination } from '@brandfolder/react';
import React, { FunctionComponent, useMemo } from 'react';

import styled from 'styled-components';

import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';

import { PeoplePage, Person } from '@components/people_tags/customPeopleTagTypes';
import { getStandardPaginationLabels } from '@translations';

import { EmptyState } from './EmptyState';
import { useFetchPeopleHubData } from './hooks/useFetchPeopleHubData';
import { PeopleManagementActionsBar } from './PeopleManagementActionsBar';
import { PeopleManagementProvider, usePeopleManagementContext } from './PeopleManagementContext';
import { PeopleManagementFilter } from './PeopleManagementFilter';
import { PeoplePageHeader } from './PeoplePageHeader';
import { PersonCard } from './PersonCard';

const PeopleTagsFaceCardsWrapper = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(1, 100%);
  justify-content: center;

  @media (min-width: 320px) {
    grid-template-columns: repeat(2, 48%);
  }
  @media (min-width: 744px) {
    grid-template-columns: repeat(2, minmax(166px, 1fr));
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(166px, 1fr));
  }
  @media (min-width: 1320px) {
    grid-template-columns: repeat(4, minmax(194px, 1fr));
  }
  @media (min-width: 1526px) {
    grid-template-columns: repeat(5, minmax(229px, 1fr));
  }
  @media (min-width: 1728px) {
    grid-template-columns: repeat(6, minmax(15%, 1fr));
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  margin-bottom: 40px;
  padding: 0 20px;
`;

const PaginationInfo = styled.div`
  font-size: 14px;
`;

const ITEMS_PER_PAGE = 24;

interface PeopleManagementContentProps {
  initialLoadingState: boolean;
  peopleData: PeoplePage[];
  peopleDataLoading: boolean;
  bfProcessed: boolean;
  allPeopleData: PeoplePage[];
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalCount: number;
  onUpdateSuccess?: () => void | Promise<void>;
}

const PeopleManagementContent: FunctionComponent<PeopleManagementContentProps> = ({
  initialLoadingState,
  peopleData,
  peopleDataLoading,
  bfProcessed,
  allPeopleData,
  totalPages,
  currentPage,
  onPageChange,
  totalCount,
  onUpdateSuccess = undefined
}) => {
  const showPageLoader = initialLoadingState || peopleDataLoading;
  const showEmptyState = !peopleData?.length && !peopleDataLoading && !initialLoadingState;

  // Transform PeoplePage[] to Person[] for autocomplete
  const availablePeople = useMemo<Person[]>(() => {
    return allPeopleData
      .filter((person) => person.name && person.name.trim() !== '')
      .map((person) => ({
        person_id: person.person_id,
        tag_name: person.name,
        face_uri: person.image_url
      }));
  }, [allPeopleData]);

  if (showPageLoader) {
    return <CircleLoader label="people-data" size={LoaderSizes.Large} />;
  }

  if (showEmptyState) {
    return <EmptyState bfProcessed={bfProcessed} />;
  }

  if (!peopleData?.length) {
    return null;
  }

  // Calculate pagination info
  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalCount);

  return (
    <>
      {totalPages > 1 && (
        <PaginationWrapper>
          <PaginationInfo>
            {`${startItem}–${endItem} of ${totalCount} ${totalCount === 1 ? 'person' : 'people'}`}
          </PaginationInfo>
          <StandardPagination
            initial={currentPage}
            labels={getStandardPaginationLabels()}
            onPageChange={onPageChange}
            total={totalPages}
          />
        </PaginationWrapper>
      )}
      <PeopleTagsFaceCardsWrapper>
        {peopleData.map((faceCard) => (
          <PersonCard
            key={faceCard.person_id}
            availablePeople={availablePeople}
            imageUrl={faceCard.image_url}
            name={faceCard.name}
            onUpdateSuccess={onUpdateSuccess ? (): void => { void onUpdateSuccess(); } : undefined}
            personId={faceCard.person_id}
          />
        ))}
      </PeopleTagsFaceCardsWrapper>
    </>
  );
};

export const PeopleManagement: FunctionComponent = () => {
  const { updateFilter, updatePage, state } = usePeopleManagementContext();
  const {
    initialLoadingState,
    peopleData,
    allPeopleData,
    peopleDataLoading,
    allCount,
    namedCount,
    unnamedCount,
    bfProcessed,
    totalPages,
    currentPage,
    refetchPeopleData,
  } = useFetchPeopleHubData();

  // Determine total count based on active filter
  const totalCount = useMemo(() => {
    switch (state.filter) {
      case 'named':
        return namedCount;
      case 'unnamed':
        return unnamedCount;
      case 'all':
      default:
        return allCount;
    }
  }, [state.filter, allCount, namedCount, unnamedCount]);

  return (
    <I18nProviderWrapper>
      <div className="people-management-wrapper">
        {/* Removed nested PeopleManagementProvider */}
        <PeoplePageHeader />
        <PeopleManagementActionsBar />
        <PeopleManagementFilter
          activeFilter={state.filter}
          allCount={allCount}
          namedCount={namedCount}
          onFilterChange={updateFilter}
          unnamedCount={unnamedCount}
        />
        <PeopleManagementContent
          allPeopleData={allPeopleData}
          bfProcessed={bfProcessed}
          currentPage={currentPage}
          initialLoadingState={initialLoadingState}
          onPageChange={updatePage}
          onUpdateSuccess={refetchPeopleData}
          peopleData={peopleData}
          peopleDataLoading={peopleDataLoading}
          totalCount={totalCount}
          totalPages={totalPages}
        />
      </div>
    </I18nProviderWrapper>
  );
};

export default (): JSX.Element => {
  return (
    <PeopleManagementProvider>
      <PeopleManagement />
    </PeopleManagementProvider>
  );
};
